import { ReactNode, useCallback } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { showContextMenu } from 'redux/mode/actions';
import { MenuItem, MenuItemAction } from 'menu/MenuItem';
import { selectIsMobile } from 'redux/mode/reducers';
import { RootReducer } from 'redux/reducers';

interface MenuProps {
	menuItems: MenuItem[] | null;
	children: ReactNode | string;
	Style: any;
	side?: 'top' | 'right' | 'bottom' | 'left';
	sideOffset?: number;
	align?: 'start' | 'center' | 'end';
}

const Menu = ({ menuItems, children, Style, side, sideOffset, align }: MenuProps) => {
	const dispatch = useDispatch();

	const { isMobile } = useSelector((r: RootReducer) => {
		return {
			isMobile: selectIsMobile(r.mode),
		};
	}, shallowEqual);

	const renderMenuItem = (item: MenuItem, index: number) => {
		const action = item as MenuItemAction;
		switch (item.kind) {
			case `MenuItemDivider`:
				return <Style.Separator key={index} />;
			case `MenuItemAction`:
				return (
					<Style.Item
						key={index}
						disabled={action.disabled}
						onSelect={action.callback}
						$isMobile={isMobile}
					>
						<div>{action.label}</div>
						<Style.Shortcut $isMobile={isMobile}>{action.shortcut}</Style.Shortcut>
						{action.showCheckmark && <Style.Checkmark $isMobile={isMobile}>✓</Style.Checkmark>}
					</Style.Item>
				);
		}
	};

	const onDismiss = useCallback(() => {
		dispatch(showContextMenu(null));
	}, [dispatch]);

	return (
		<Style.Root>
			<Style.Trigger>{children}</Style.Trigger>
			<Style.Portal>
				<Style.Content
					$isMobile={isMobile}
					onInteractOutside={onDismiss}
					onContextMenu={(e: { preventDefault: () => any }) => e.preventDefault()}
					onMouseLeave={isMobile ? undefined : onDismiss}
					side={side ?? 'bottom'}
					sideOffset={sideOffset ?? 0}
					align={align ?? 'start'}
				>
					{menuItems?.map((item, index: number) => renderMenuItem(item, index))}
				</Style.Content>
			</Style.Portal>
		</Style.Root>
	);
};

export default Menu;
